import { useState, useEffect, useRef } from 'react';
import { X, Search, Calendar, Plane } from 'lucide-react';
import { fsopApi } from '@/services/api';
import type { FlightLog } from '@/services/api';

interface FlightLogSelectorProps {
  selectedFlightLogId?: number | null;
  onFlightLogChange: (flightLogId: number | null) => void;
  className?: string;
  disabled?: boolean;
}

export function FlightLogSelector({
  selectedFlightLogId,
  onFlightLogChange,
  className,
  disabled,
}: FlightLogSelectorProps) {
  const [logs, setLogs] = useState<FlightLog[]>([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedFlightLogId) return;
    // 如果当前选中项不在搜索列表中，单独加载详情
    if (!logs.some((l) => l.id === selectedFlightLogId)) {
      fsopApi
        .getFlightLog(selectedFlightLogId)
        .then((res) => {
          const log = res.data.data;
          if (log) {
            setLogs((prev) => {
              if (prev.some((l) => l.id === log.id)) return prev;
              return [log, ...prev];
            });
          }
        })
        .catch(() => {
          // 加载失败时静默处理，仍显示未关联
        });
    }
  }, [selectedFlightLogId]);

  useEffect(() => {
    if (!search.trim()) {
      setLogs([]);
      return;
    }
    const timer = setTimeout(() => {
      setLoading(true);
      fsopApi
        .getFlightLogs({ search: search.trim(), pageSize: 20 })
        .then((res: any) => {
          setLogs(res.data.results || []);
          setShowDropdown(true);
        })
        .catch(() => setLogs([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const selectedLog = logs.find(l => l.id === selectedFlightLogId);

  return (
    <div ref={containerRef} className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <Calendar className="w-4 h-4 inline mr-1" />
        飞行记录
      </label>

      {selectedFlightLogId && selectedLog ? (
        <div className={`flex items-center gap-2 p-2 border rounded-lg ${disabled ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`}>
          <Plane className={`w-4 h-4 ${disabled ? 'text-gray-400' : 'text-amber-600'}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${disabled ? 'text-gray-700' : 'text-amber-800'}`}>
              {selectedLog.airNum || '未知航班'}
            </p>
            <p className={`text-xs ${disabled ? 'text-gray-500' : 'text-amber-600'}`}>
              {selectedLog.airDate} · {selectedLog.departure}-{selectedLog.arrival}
              {selectedLog.recordType ? ` · ${selectedLog.recordType}` : ''}
            </p>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => onFlightLogChange(null)}
              className="p-1 hover:bg-amber-100 rounded flex-shrink-0"
            >
              <X className="w-4 h-4 text-amber-600" />
            </button>
          )}
        </div>
      ) : disabled ? (
        <div className="px-3 py-2 text-sm text-gray-400 border border-gray-200 rounded-lg bg-gray-50">
          未关联飞行记录
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => { if (logs.length > 0) setShowDropdown(true); }}
            placeholder="搜索航班号、日期..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            </div>
          )}
          {showDropdown && logs.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {logs.map(log => (
                <button
                  key={log.id}
                  type="button"
                  onClick={() => {
                    onFlightLogChange(log.id);
                    setSearch('');
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <Plane className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {log.airNum || '未知航班'}
                    </span>
                    <span className="text-xs text-gray-500 ml-auto">
                      {log.airDate}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 pl-5.5">
                    {log.departure}-{log.arrival}
                    {log.airModel ? ` · ${log.airModel}` : ''}
                    {log.recordType ? ` · ${log.recordType}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
          {showDropdown && search.trim() && !loading && logs.length === 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-4 text-sm text-gray-500 text-center">
              未找到匹配的飞行记录
            </div>
          )}
        </div>
      )}
    </div>
  );
}
